/**
 * Spacing system for Equine Performance Labs
 * 
 * Based on 8px grid system for consistency
 */

export const spacing = {
  // Base unit (used by MUI spacing function)
  unit: 8,
  
  // Named spacing values
  values: {
    none: 0,
    xxs: 4,    // 0.5 * 8
    xs: 8,     // 1 * 8
    sm: 12,    // 1.5 * 8
    md: 16,    // 2 * 8
    lg: 24,    // 3 * 8
    xl: 32,    // 4 * 8
    xxl: 40,   // 5 * 8
    xxxl: 48,  // 6 * 8
    huge: 64,  // 8 * 8
    giant: 80, // 10 * 8
    mega: 96,  // 12 * 8
    giga: 128, // 16 * 8
  },
  
  // Component-specific spacing
  components: {
    // Cards
    cardPadding: {
      compact: 16,
      default: 24,
      spacious: 32,
    },
    
    // Buttons
    buttonPadding: {
      small: '6px 16px',
      medium: '10px 24px',
      large: '12px 32px',
    },
    
    // Forms
    formSpacing: {
      fieldGap: 16,
      sectionGap: 32,
      labelGap: 8,
    },
    
    // Tables
    tablePadding: {
      cell: '16px',
      cellCompact: '12px',
      cellSpacious: '20px',
    },
    
    // Navigation
    navSpacing: {
      itemGap: 8,
      sectionGap: 24,
      iconGap: 12,
    },
  },
  
  // Page layout spacing
  layout: {
    pageMargin: {
      mobile: 16,
      tablet: 24,
      desktop: 32,
    },
    
    sectionSpacing: {
      small: 32,
      medium: 48,
      large: 64,
      xlarge: 96,
    },
    
    containerPadding: {
      mobile: 16,
      tablet: 24,
      desktop: 32,
    },
    
    gridGap: {
      small: 16,
      medium: 24,
      large: 32,
    },
  },
  
  // Healthcare-specific spacing
  healthcare: {
    // Patient cards
    patientCard: {
      padding: 20,
      headerPadding: 16,
      sectionGap: 24,
    },
    
    // Medication lists
    medicationList: {
      itemGap: 12,
      dosageIndent: 24,
    },
    
    // Charts and graphs
    chartSpacing: {
      padding: 16,
      legendGap: 12,
      axisOffset: 8,
    },
  },
};

// Spacing utilities
export const spacingUtils = {
  // Margin helpers
  margin: (value) => ({
    margin: spacing.values[value] || value,
  }),
  
  marginTop: (value) => ({
    marginTop: spacing.values[value] || value,
  }),
  
  marginRight: (value) => ({
    marginRight: spacing.values[value] || value,
  }),
  
  marginBottom: (value) => ({
    marginBottom: spacing.values[value] || value,
  }),
  
  marginLeft: (value) => ({
    marginLeft: spacing.values[value] || value,
  }),
  
  marginX: (value) => ({
    marginLeft: spacing.values[value] || value,
    marginRight: spacing.values[value] || value,
  }),
  
  marginY: (value) => ({
    marginTop: spacing.values[value] || value,
    marginBottom: spacing.values[value] || value,
  }),
  
  // Padding helpers
  padding: (value) => ({
    padding: spacing.values[value] || value,
  }),
  
  paddingTop: (value) => ({
    paddingTop: spacing.values[value] || value,
  }),
  
  paddingRight: (value) => ({
    paddingRight: spacing.values[value] || value,
  }),
  
  paddingBottom: (value) => ({
    paddingBottom: spacing.values[value] || value,
  }),
  
  paddingLeft: (value) => ({
    paddingLeft: spacing.values[value] || value,
  }),
  
  paddingX: (value) => ({
    paddingLeft: spacing.values[value] || value,
    paddingRight: spacing.values[value] || value,
  }),
  
  paddingY: (value) => ({
    paddingTop: spacing.values[value] || value,
    paddingBottom: spacing.values[value] || value,
  }),
  
  // Gap helpers (for flexbox/grid)
  gap: (value) => ({
    gap: spacing.values[value] || value,
  }),
  
  rowGap: (value) => ({
    rowGap: spacing.values[value] || value,
  }),
  
  columnGap: (value) => ({
    columnGap: spacing.values[value] || value,
  }),
};

// Responsive spacing
export const responsiveSpacing = {
  padding: {
    xs: spacing.values.sm,
    sm: spacing.values.md,
    md: spacing.values.lg,
    lg: spacing.values.xl,
    xl: spacing.values.xxl,
  },
  
  margin: {
    xs: spacing.values.xs,
    sm: spacing.values.sm,
    md: spacing.values.md,
    lg: spacing.values.lg,
    xl: spacing.values.xl,
  },
};
